import { sentryService } from '@/services/SentryService';

export const LOADS_KEY = 'llReloadGuardLoads';
export const TRIPPED_KEY = 'llReloadGuardTripped';
export const MAX_LOADS = 6;
export const WINDOW_MS = 30_000;

interface TripRecord {
  loads: number;
  spanMs: number;
}

function readLoads(storage: Storage, now: number): number[] {
  try {
    const parsed = JSON.parse(storage.getItem(LOADS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t) => typeof t === 'number' && now - t >= 0 && now - t < WINDOW_MS);
  } catch {
    return [];
  }
}

/** Records this page load; returns true when the tab has reloaded too often to keep booting. */
export function recordLoadAndCheck(storage: Storage, now: number): boolean {
  const loads = [...readLoads(storage, now), now];
  storage.setItem(LOADS_KEY, JSON.stringify(loads));
  if (loads.length < MAX_LOADS) return false;

  const record: TripRecord = { loads: loads.length, spanMs: now - loads[0] };
  storage.setItem(TRIPPED_KEY, JSON.stringify(record));
  return true;
}

export function reportPreviousTrip(storage: Storage): void {
  if (!storage.getItem(TRIPPED_KEY)) return;
  // Sentry initialises late in boot, after auth; the record stays until then so a load that
  // redirects away first (login, the loop recurring) leaves it for the next load to report.
  sentryService.whenReady(() => {
    const raw = storage.getItem(TRIPPED_KEY);
    if (!raw) return;
    storage.removeItem(TRIPPED_KEY);
    let record: Partial<TripRecord> = {};
    try {
      record = JSON.parse(raw);
    } catch {
      // report with no detail rather than not at all
    }
    sentryService.captureException(new Error('Reload loop guard tripped'), {
      feature: 'reload-loop-guard',
      loads: record.loads,
      span_ms: record.spanMs,
    });
  });
}

function showStoppedScreen(storage: Storage): void {
  const render = () => {
    const overlay = document.createElement('div');
    overlay.setAttribute('id', 'll-reload-loop-guard');
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-labelledby', 'll-reload-loop-guard-title');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:10001;background:#f5f5f5;display:flex;' +
      'align-items:center;justify-content:center;font-family:sans-serif;';

    const card = document.createElement('div');
    card.style.cssText =
      'background:#fff;border-radius:8px;max-width:440px;width:calc(100vw - 48px);' +
      'padding:24px;text-align:center;box-shadow:0 4px 20px rgba(0,0,0,0.15);color:#333;';

    const title = document.createElement('h1');
    title.setAttribute('id', 'll-reload-loop-guard-title');
    title.textContent = 'This page keeps reloading';
    title.style.cssText = 'font-size:18px;margin:0 0 12px;color:#111;';

    const body = document.createElement('p');
    body.textContent =
      'LeanLaw stopped loading because this window reloaded itself many times in a few seconds. ' +
      'If you opened LeanLaw in another app or a saved web app, try a regular browser tab instead.';
    body.style.cssText = 'font-size:14px;line-height:1.6;margin:0 0 20px;';

    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.textContent = 'Reload';
    button.style.cssText =
      'min-width:160px;padding:10px 20px;background:#29bf12;border:none;border-radius:5px;' +
      'cursor:pointer;font-size:14px;color:#fff;font-weight:500;';
    button.addEventListener('click', () => {
      try {
        storage.removeItem(LOADS_KEY);
      } catch {
        // storage gone - reload anyway
      }
      window.location.reload();
    });

    card.append(title, body, button);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  };

  if (document.body) render();
  else document.addEventListener('DOMContentLoaded', render, { once: true });
}

let trippedThisLoad = false;

/** Startup code that runs outside Angular (polls, auto-reloads) checks this to stand down. */
export function isReloadGuardTripped(): boolean {
  return trippedThisLoad;
}

/** Runs before any app code. Removing ng-app stops AngularJS auto-bootstrap and its startup API calls. */
export function installReloadLoopGuard(): boolean {
  let storage: Storage;
  let tripped: boolean;
  try {
    storage = window.sessionStorage;
    tripped = recordLoadAndCheck(storage, Date.now());
  } catch {
    return false;
  }

  if (!tripped) {
    try {
      reportPreviousTrip(storage);
    } catch {
      // never block boot on reporting
    }
    return false;
  }

  trippedThisLoad = true;
  document.documentElement.removeAttribute('ng-app');
  // Angular normally strips ng-cloak on bootstrap; left in place it hides the whole page.
  document.documentElement.removeAttribute('ng-cloak');
  showStoppedScreen(storage);
  return true;
}

installReloadLoopGuard();
